import { defineStore } from 'pinia';
import { removeEmpty, sitecoreVehicleType } from '../../../foundation/helpers';
import { getters } from './getters';
import { useAnalytics } from '../../../composables/useAnalytics';
import { AnalyticsEventNames, AnalyticsEventTypes, AnalyticsEvents } from '../../enums/AnalyticsEvents';
import { useUrlBuilder } from '../../../composables/useUrlBuilder';
import { carAdvisoryService } from '../../apiCarAdvisoryService';
import { hasStoreContentExpired } from '../../../foundation/helpers';
export const MAX_LIMIT = 3;
const analytics = useAnalytics();
export interface ICarComparisonStore {
  selectedVehicleIds: string[];
  showCarComparisonFooter: boolean;
  fetchedVehicles: { [id: string]: any };
  maxNumVehiclesInStore: number;
  showCarComparisonMobileInfoOverlay: boolean;
  userHasAcceptedMobileInfoOverlay: boolean;
  storeLastRefreshed: string; //the datetime when store has been last reset - cache cleared
}

export const useCarComparisonStore = defineStore('carComparisonStore', {
  state: (): ICarComparisonStore => ({
    // When a user adds a vehicle to the comparison cart, we store the vehicle ID here
    selectedVehicleIds: ['', '', ''],
    // Boolean flag to show/hide the sticky comparison cart footer
    showCarComparisonFooter: false,
    // Store the vehicles fetched from the API here, this store persists in local storage
    fetchedVehicles: {},
    // Set a (conservative) max num of vehicles to not exceed the 5MB local storage limit
    maxNumVehiclesInStore: 300,
    // Boolean flag to show/hide the mobile info/help text overlay
    showCarComparisonMobileInfoOverlay: false,
    // Check whether the user has seen and accepted the mobile info/help text overlay
    userHasAcceptedMobileInfoOverlay: false,
    //the datetime when store has been last reset - cache cleared
    storeLastRefreshed: '',
  }),
  actions: {
    /**
     * Hide or show the car comparison mobile overlay.
     *
     * @param toggleState {Boolean}
     */
    toggleMobileOverlay(toggleState: boolean = false) {
      if (this.userHasAcceptedMobileInfoOverlay || !toggleState) {
        this.showCarComparisonMobileInfoOverlay = false;
        return;
      }
      if (toggleState) {
        this.showCarComparisonMobileInfoOverlay = toggleState;
      }
    },
    /**
     * If store needs to be refreshed because of the expiry time.
     *
     * @param numberOfHoursForStoreContentToExpire
     */
    resetStoreCache(numberOfHoursForStoreContentToExpire: number) {
      if (this.storeLastRefreshed) {
        // if last updated date is set in the store
        const hasContentExpired = hasStoreContentExpired(this.storeLastRefreshed, numberOfHoursForStoreContentToExpire);

        if (hasContentExpired) {
          // if last updated time + config hours in less than current system datetime - then store require updating
          console.log('Store content refreshed => Car Comparison store');
          this.$reset();
          this.storeLastRefreshed = new Date().toString();
        }
      } else {
        console.log('Setting last updated on store => Car Comparison store');
        this.storeLastRefreshed = new Date().toString();
      }
    },
    /**push data to analytics */
    pushToAnalytics(vehicleId: string, event: AnalyticsEvents, name: string, type: AnalyticsEventTypes, targetUrl?: string) {
      try {
        const car = this.fetchedVehicles[vehicleId];
        if (car) {
          const eventData = {
            eventInfo: {
              navigation: {
                name: name,
                type: type,
                makeName: car.Make,
                modelName: car.Model,
                badgeName: car.Trim,
                year: `${car.Year_low} - ${car.Year_high}`,
                bodyType: car.Body_type,
                engineType: car.Overall_EngineType,
                URL: useUrlBuilder().getCarDetailsUrl(car.Vehicle_UID, `${car.Year_low} - ${car.Year_high}`, car.Make, car.Model),
                targetUrl: targetUrl,
              },
            },
          };
          analytics.push(event, eventData);
        }
      } catch (error) {
        console.warn('fail to push data to analytics', error);
      }
    },
    /**
     * Simply replace the selected vehicle ID with a new ID.
     *
     * @param index
     * @param vehicleId
     */
    updateCarToCompare(index: number, vehicleId: string) {
      this.selectedVehicleIds[index] = vehicleId ?? '';
    },

    /**
     * Add a car to compare.
     *
     * @param vehicleId The ID of the vehicle to compare.
     * @param options   An options object
     */
    async addCarToCompare(vehicleId: string, options = { showCart: true }) {
      if (this.vehicleIdsInCart.includes(vehicleId) || this.vehicleIdsInCart.length >= MAX_LIMIT) return;
      this.showCarComparisonFooter = false;
      const emptyVehicleIndex = this.selectedVehicleIds.indexOf('');

      if (emptyVehicleIndex > -1) {
        this.selectedVehicleIds[emptyVehicleIndex] = vehicleId;
      }

      if (options.showCart) {
        this.showCarComparisonFooter = true;
      }
      if (!this.fetchedVehicles[vehicleId]) {
        const data: Record<string, any> = await carAdvisoryService.fetchVehicleById(vehicleId);
        this.updateFetchedVehicles(vehicleId, data);
      }
      this.pushToAnalytics(vehicleId, AnalyticsEvents.NavigationClickToCompare, AnalyticsEventNames.CompareFromResults, AnalyticsEventTypes.CarResults);
    },
    /**
     * Remove a car from comparison.
     *
     * @param index       The car's index in the collection.
     * @param showFooter  Show or hide the comparison cart.
     */
    removeCarToCompare(index: number, showFooter = false) {
      this.selectedVehicleIds[index] = '';
      this.showCarComparisonFooter = showFooter;
    },
    /**
     * Update the fetched vehicles collection. This collection
     * stores vehicle data that has been fetched by the API.
     *
     * @param vehicleId The ID of the vehicle to fetch.
     * @param data      The vehicle data from the API.
     */
    updateFetchedVehicles(vehicleId: string, data: Record<string, any>) {
      // Purge unused vehicles from the store if fetch limit exceeded
      const maxVehicleCountReached = this.fetchedVehiclesCount >= this.maxNumVehiclesInStore;

      if (maxVehicleCountReached) {
        this.purgeFetchedVehicles();
      }

      // Computed property for use in conditional rendering of vehicle data
      if (data && Object.keys(data).length > 0) {
        data.RACQ_SC_Vehicle_Type = sitecoreVehicleType(data, false);
        data.RACQ_PRICE = data.Status.toLowerCase() === 'current' ? data?.QLD_DRIVEAWAYPRICE || 'N/A' : data?.Current_retail_value_Average || 'N/A';

        this.fetchedVehicles[vehicleId] = data;
      }
      this.fetchedVehicles = removeEmpty(this.fetchedVehicles);
    },
    /**
     * This helper function clears the fetchedVehicles store object
     * and keeps only those in the current compare cart.
     *
     * It is used to ensure the local storage limit is never reached,
     * seeing as we persist our pinia store state.
     */
    purgeFetchedVehicles() {
      function returnOnlyValidKeys(obj: any, validKeys: string[]) {
        const newObject: { [id: string]: any } = {};

        Object.keys(obj).forEach((key) => {
          if (validKeys.includes(key)) newObject[key] = obj[key];
        });

        return newObject;
      }

      this.fetchedVehicles = returnOnlyValidKeys(this.fetchedVehicles, this.selectedVehicleIds);
    },
  },
  // Importing getters and types to make TS happy
  // ref: https://github.com/vuejs/pinia/discussions/983
  getters: { ...getters },

  persist: true,
});
